import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  IconButton,
  Tabs,
  Tab,
  CircularProgress,
  Alert
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { fr } from 'date-fns/locale';
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  addMonths,
  getMonth,
  getYear,
  isWithinInterval,
  getDay
} from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { ArrowBack as ArrowBackIcon, CalendarToday as CalendarIcon, GetApp as GetAppIcon } from '@mui/icons-material';
import { db } from '../../firebase/config';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import Holidays from 'date-holidays';

// Fonction pour générer les jours fériés pour une année donnée
const generateHolidays = (startYear, endYear) => {
  try {
    const hd = new Holidays('FR');
    
    // Récupérer les jours fériés pour l'année scolaire (juillet à août)
    const startYearHolidays = hd.getHolidays(startYear);
    const endYearHolidays = hd.getHolidays(endYear);
    
    // Convertir les dates en format yyyy-MM-dd pour faciliter la comparaison
    const formattedStartYearHolidays = startYearHolidays.map(h => ({
      ...h,
      date: format(new Date(h.date), 'yyyy-MM-dd')
    }));
    
    const formattedEndYearHolidays = endYearHolidays.map(h => ({
      ...h,
      date: format(new Date(h.date), 'yyyy-MM-dd')
    }));
    
    // Filtrer pour ne garder que les jours fériés de l'année scolaire
    // (de juillet de l'année de début à août de l'année de fin)
    const filteredHolidays = [
      ...formattedStartYearHolidays.filter(h => new Date(h.date).getMonth() >= 6), // Juillet à décembre
      ...formattedEndYearHolidays.filter(h => new Date(h.date).getMonth() <= 7)  // Janvier à août
    ];
    
    // Trier les jours fériés par date
    filteredHolidays.sort((a, b) => new Date(a.date) - new Date(b.date));
    
    
    return filteredHolidays;
  } catch (error) {
    console.error('Erreur lors de la génération des jours fériés:', error);
    return [];
  }
};

const DAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

const SchoolCalendar = () => {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentDate, setCurrentDate] = useState(null);
  const [vacationPeriods, setVacationPeriods] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [establishmentInfo, setEstablishmentInfo] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [showHolidaysInCalendar, setShowHolidaysInCalendar] = useState(true);

  // Fonction pour récupérer les périodes de vacances
  const fetchVacationPeriods = async () => {
    try {
      setLoading(true);
      
      if (!userData?.establishmentId) {
        setLoading(false);
        return;
      }

      const vacancesRef = collection(db, 'establishments', userData.establishmentId, 'vacances');
      
      const snapshot = await getDocs(vacancesRef);
      
      const periods = snapshot.docs.map(doc => {
        const data = doc.data();
        
        if (!data.debut || !data.fin) {
          console.error('Période invalide - dates manquantes:', data);
          return null;
        }

        const period = {
          id: doc.id,
          ...data,
          start: data.debut.toDate(),
          end: data.fin.toDate(),
          name: data.nom,
          type: data.type
        };
        
        return period;
      }).filter(Boolean);

      setVacationPeriods(periods);
      setLoading(false);
    } catch (err) {
      console.error('Erreur détaillée:', err);
      console.error('Stack trace:', err.stack);
      setError('Erreur lors du chargement des périodes de vacances');
      setLoading(false);
    }
  };

  // Récupérer les informations de l'établissement
  const fetchEstablishmentInfo = async () => {
    try {
      if (!userData?.establishmentId) {
        return;
      }

      const establishmentDoc = await getDoc(doc(db, 'establishments', userData.establishmentId));
      if (establishmentDoc.exists()) {
        const data = establishmentDoc.data();
        setEstablishmentInfo(data);
        
        // Récupérer la préférence d'affichage des jours fériés
        setShowHolidaysInCalendar(data.showHolidaysInCalendar !== undefined ? data.showHolidaysInCalendar : true);
      }
    } catch (err) {
      console.error('Erreur lors de la récupération des informations de l\'établissement:', err);
    }
  };

  // Fonction pour charger les jours fériés
  const loadHolidays = () => {
    if (!establishmentInfo) return;
    
    const startYear = establishmentInfo.anneeDebut || new Date().getFullYear();
    const endYear = establishmentInfo.anneeFin || startYear + 1;
    
    try {
      const holidaysList = generateHolidays(startYear, endYear);
      setHolidays(holidaysList);
    } catch (error) {
      console.error('Erreur lors du chargement des jours fériés:', error);
    }
  };

  // Vérifier si une date est un jour férié
  const isHoliday = (date) => {
    if (!showHolidaysInCalendar || !holidays || holidays.length === 0) return null;
    
    const formattedDate = format(date, 'yyyy-MM-dd');
    
    return holidays.find(holiday => holiday.date === formattedDate);
  };

  // Vérifier si une date est pendant les vacances
  const isVacation = (date) => {
    if (!date || !vacationPeriods || !vacationPeriods.length) return null;

    try {
      return vacationPeriods.find(period => {
        // Vérifier que les dates de début et de fin sont valides
        if (!period.start || !period.end || !(period.start instanceof Date) || !(period.end instanceof Date)) {
          return false;
        }
        
        // Vérifier que la date de fin est après la date de début
        if (period.end < period.start) {
          console.error('Période invalide: la date de fin est antérieure à la date de début', period);
          return false;
        }
        
        try {
          return isWithinInterval(date, { start: period.start, end: period.end });
        } catch (error) {
          console.error('Erreur dans isWithinInterval:', error, period);
          return false;
        }
      });
    } catch (error) {
      console.error('Erreur générale dans isVacation:', error);
      return null;
    }
  };

  // Obtenir la couleur de fond en fonction du type de vacances
  const getVacationColor = (type) => {
    switch(type?.toUpperCase()) {
      case 'PRINTEMPS': return '#a5d6a7';
      case 'ETE': return '#fff59d';
      case 'AUTOMNE': return '#ffcc80';
      case 'HIVER': return '#90caf9';
      default: return '#e0e0e0';
    }
  };

  // Obtenir la couleur de bordure en fonction du type de vacances
  const getVacationBorderColor = (type) => {
    switch(type?.toUpperCase()) {
      case 'PRINTEMPS': return '#2e7d32';
      case 'ETE': return '#fbc02d';
      case 'AUTOMNE': return '#f57c00';
      case 'HIVER': return '#1976d2';
      default: return '#9e9e9e';
    }
  };

  // Récupérer les informations au chargement
  useEffect(() => {
    const loadData = async () => {
      if (userData?.establishmentId) {
        await fetchEstablishmentInfo();
        await fetchVacationPeriods();
      }
    };
    loadData();
  }, [userData]);

  // Mettre à jour la date de début en fonction de l'année scolaire
  useEffect(() => {
    if (establishmentInfo?.anneeScolaire) {
      const year = parseInt(establishmentInfo.anneeScolaire.split('-')[0]);
      if (!isNaN(year)) {
        setCurrentDate(new Date(year, 6, 1)); // Juillet de l'année spécifiée
      }
    }
  }, [establishmentInfo]);
  
  // Charger les jours fériés quand les informations de l'établissement changent
  useEffect(() => {
    if (establishmentInfo) {
      loadHolidays();
    }
  }, [establishmentInfo, showHolidaysInCalendar]);

  // Générer les mois de l'année scolaire en fonction des paramètres
  const months = [];
  
  // Récupérer les mois et années de début et de fin depuis les paramètres de l'établissement
  const moisDebut = establishmentInfo?.moisDebut !== undefined ? establishmentInfo.moisDebut : 6; // Juillet par défaut
  const moisFin = establishmentInfo?.moisFin !== undefined ? establishmentInfo.moisFin : 7; // Août par défaut
  const anneeDebut = establishmentInfo?.anneeDebut !== undefined ? establishmentInfo.anneeDebut : new Date().getFullYear();
  const anneeFin = establishmentInfo?.anneeFin !== undefined ? establishmentInfo.anneeFin : new Date().getFullYear();
  
  // Date de début et de fin
  const dateDebut = new Date(anneeDebut, moisDebut, 1);
  const dateFin = new Date(anneeFin, moisFin, 1);
  
  // Calculer le nombre de mois entre les deux dates
  const diffAnnees = anneeFin - anneeDebut;
  const diffMois = moisFin - moisDebut;
  const nombreMois = diffAnnees * 12 + diffMois + 1; // +1 pour inclure le mois de fin
  
  // Générer tous les mois entre la date de début et la date de fin
  for (let i = 0; i < nombreMois; i++) {
    // Commencer par le mois de début de l'année scolaire
    let year = anneeDebut;
    let month = moisDebut + i;
    
    // Si le mois dépasse décembre, ajuster l'année et le mois
    if (month > 11) {
      const anneesSupplement = Math.floor(month / 12);
      month = month % 12;
      year = year + anneesSupplement;
    }
    
    const monthDate = new Date(year, month, 1);
    months.push(monthDate);
  }

  // Générer le calendrier pour un mois
  const renderMonth = (date) => {
    const start = startOfMonth(date);
    const end = endOfMonth(date);
    const days = eachDayOfInterval({ start, end });
    // Obtenir l'index du jour (0 = dimanche, 1 = lundi, ..., 6 = samedi)
    let startingDayIndex = getDay(start);
    // Convertir pour que lundi soit 0 et dimanche soit 6
    startingDayIndex = startingDayIndex === 0 ? 6 : startingDayIndex - 1;
    
    return (
      <Box sx={{ mb: 2 }}>
        <Typography 
          variant="h6" 
          align="center" 
          gutterBottom
          sx={{
            textTransform: 'capitalize',
            fontWeight: 'bold',
            color: '#1976d2',
            mb: 2
          }}
        >
          {format(date, 'MMMM yyyy', { locale: fr })}
        </Typography>
        <Grid container spacing={1}>
          {/* En-têtes des jours */}
          {DAYS.map((day, index) => (
            <Grid item xs={12/7} key={`header-${index}`}>
              <Box sx={{
                p: 0.5,
                textAlign: 'center',
                fontWeight: 'bold',
                fontSize: '0.75rem'
              }}>
                {day}
              </Box>
            </Grid>
          ))}

          {/* Cellules vides pour le début du mois */}
          {Array.from({ length: startingDayIndex }).map((_, index) => (
            <Grid item xs={12/7} key={`empty-${index}`}>
              <Box sx={{ 
                minHeight: '50px',
                border: '1px solid transparent'
              }} />
            </Grid>
          ))}
          
          {/* Jours du mois */}
          {days.map(day => (
            <Grid item xs={12/7} key={format(day, 'yyyy-MM-dd')}>
              <Box
                className="calendar-cell"
                sx={{
                  position: 'relative',
                  minHeight: '50px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  padding: '4px',
                  border: (theme) => {
                    const vacation = isVacation(day);
                    if (vacation) return `1px solid ${getVacationBorderColor(vacation.type)}`;
                    if (isHoliday(day)) return `1px solid ${theme.palette.error.main}`;
                    return `1px solid ${theme.palette.divider}`;
                  },
                  borderRadius: '8px',
                  backgroundColor: (() => {
                    const vacation = isVacation(day);
                    if (isHoliday(day)) return '#ffebee';
                    if (vacation) return getVacationColor(vacation.type);
                    return 'transparent';
                  })(),
                  fontSize: '0.85rem',
                  fontWeight: isHoliday(day) || isVacation(day) ? 'bold' : 'normal',
                  '&:hover': {
                    backgroundColor: (() => {
                      const vacation = isVacation(day);
                      if (isHoliday(day)) return '#ffcdd2';
                      if (vacation) {
                        const baseColor = getVacationColor(vacation.type);
                        return baseColor.replace(/[^,]+(?=\))/, '0.8');
                      }
                      return '#f5f5f5';
                    })(),
                  },
                  boxShadow: isVacation(day) || isHoliday(day) ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
                  transform: isVacation(day) || isHoliday(day) ? 'scale(1.02)' : 'scale(1)',
                  transition: 'all 0.2s ease-in-out',
                  cursor: 'default'
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 'bold',
                    color: isHoliday(day) ? '#d32f2f' : 'inherit'
                  }}
                >
                  {format(day, 'd')}
                </Typography>
                {(isHoliday(day) || isVacation(day)) && (
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '0.65rem',
                      textAlign: 'center',
                      color: isHoliday(day) ? '#d32f2f' : getVacationBorderColor(isVacation(day)?.type),
                      mt: '2px',
                      lineHeight: 1
                    }}
                  >
                    {isHoliday(day) 
                      ? holidays.find(h => h.date === format(day, 'yyyy-MM-dd'))?.name
                      : isVacation(day)?.nom
                    }
                  </Typography>
                )}
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  // Fonction pour exporter le calendrier en PDF
  const exportToPDF = async () => {
    try {
      setLoading(true);
      const calendarElement = document.getElementById('calendar');
      
      if (!calendarElement) {
        console.error('Élément calendrier non trouvé');
        return;
      }

      // Créer le PDF au format A4 en portrait
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      // Dimensions de la page A4 en portrait
      const pageWidth = 210;
      const pageHeight = 297;
      const margin = 10;

      // Ajouter le titre avec le nom de l'établissement
    const schoolName = establishmentInfo?.nom || establishmentInfo?.nom_etablissement || "Calendrier scolaire";
    
    // Ajouter le logo à gauche et à droite
    let logoHeight = 0;
    if (establishmentInfo?.logo && establishmentInfo.logo.startsWith('data:image/')) {
      try {
        // Dimensions du logo
        const logoWidth = 25;
        logoHeight = 15;
        
        // Logo à gauche
        pdf.addImage(establishmentInfo.logo, 'AUTO', margin, margin, logoWidth, logoHeight);
        
        // Logo à droite
        pdf.addImage(establishmentInfo.logo, 'AUTO', pageWidth - margin - logoWidth, margin, logoWidth, logoHeight);
      } catch (logoError) {
        console.error('Erreur lors de l\'ajout du logo:', logoError);
        // Continuer sans logo en cas d'erreur
        logoHeight = 0;
      }
    }
    
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(16);
    pdf.setTextColor(0, 0, 0); // Noir
      
      // Titre principal
      const mainTitle = "Calendrier scolaire";
      const mainTitleWidth = pdf.getStringUnitWidth(mainTitle) * 16 / pdf.internal.scaleFactor;
      const mainTitleX = (pageWidth - mainTitleWidth) / 2;
      pdf.text(mainTitle, mainTitleX, margin + 10 + logoHeight);

      // Nom de l'établissement
      pdf.setFontSize(14);
      const schoolNameWidth = pdf.getStringUnitWidth(schoolName) * 14 / pdf.internal.scaleFactor;
      const schoolNameX = (pageWidth - schoolNameWidth) / 2;
      pdf.text(schoolName, schoolNameX, margin + 20 + logoHeight);

      // Année scolaire
      const yearText = "Année scolaire 2024-2025";
      pdf.setFontSize(12);
      const yearWidth = pdf.getStringUnitWidth(yearText) * 12 / pdf.internal.scaleFactor;
      const yearX = (pageWidth - yearWidth) / 2;
      pdf.text(yearText, yearX, margin + 28 + logoHeight);

      // Calculer la zone disponible pour le calendrier
      const availableWidth = pageWidth - (2 * margin);
      const availableHeight = pageHeight - (2 * margin) - 30; // 30mm pour l'en-tête

      // Créer un canvas avec les dimensions optimisées
      const canvas = await html2canvas(calendarElement, {
        scale: 2, // Meilleure qualité
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        windowWidth: 1400, // Largeur réduite pour une meilleure adaptation au format portrait
        onclone: (document) => {
          // Ajuster le style du clone pour l'export
          const clonedElement = document.getElementById('calendar');
          if (clonedElement) {
            clonedElement.style.transform = 'scale(0.95)';
            clonedElement.style.transformOrigin = 'top left';
            // Optimiser les cellules pour le format portrait
            const cells = clonedElement.getElementsByClassName('calendar-cell');
            Array.from(cells).forEach(cell => {
              cell.style.padding = '2px';
              cell.style.fontSize = '8px';
              cell.style.minHeight = '40px';
            });
            // Optimiser les en-têtes des mois
            const monthHeaders = clonedElement.getElementsByTagName('h6');
            Array.from(monthHeaders).forEach(header => {
              header.style.fontSize = '12px';
              header.style.marginBottom = '4px';
            });
            // Masquer les titres originaux du calendrier pour éviter la duplication
            const titles = clonedElement.getElementsByTagName('h1');
            Array.from(titles).forEach(title => {
              title.style.display = 'none';
            });
          }
        }
      });

      // Calculer les dimensions pour que l'image tienne dans la zone disponible
      const imgWidth = availableWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // Si l'image est trop haute, ajuster la largeur en fonction de la hauteur
      if (imgHeight > availableHeight) {
        const ratio = availableHeight / imgHeight;
        const newWidth = imgWidth * ratio;
        const newHeight = availableHeight;
        const xOffset = (pageWidth - newWidth) / 2;
        const imgData = canvas.toDataURL('image/png');
        pdf.addImage(imgData, 'PNG', xOffset, margin + 25, newWidth, newHeight);
      } else {
        // Centrer l'image horizontalement
        const xOffset = (pageWidth - imgWidth) / 2;
        const imgData = canvas.toDataURL('image/png');
        pdf.addImage(imgData, 'PNG', xOffset, margin + 25, imgWidth, imgHeight);
      }

      // Ajouter une ligne de séparation pour le pied de page
      pdf.setDrawColor(200, 200, 200); // Gris clair
      pdf.line(margin, pageHeight - 15, pageWidth - margin, pageHeight - 15);
      
      // Ajouter le texte de copyright au centre du pied de page
      pdf.setFontSize(10);
      pdf.setTextColor(100, 100, 100); // Gris foncé
      pdf.setFont("helvetica", "italic");
      const copyrightText = "Edugestion.softmarket.io";
      pdf.text(copyrightText, pageWidth / 2, pageHeight - 10, { align: 'center' });

      // Générer le nom du fichier
      const fileName = `Calendrier_${schoolName.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${establishmentInfo?.anneeScolaire || 'Juillet_2024-Aout_2025'}.pdf`;
      
      // Sauvegarder le PDF
      pdf.save(fileName);
      
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors de l\'export PDF:', error);
      setError('Erreur lors de l\'export du calendrier en PDF');
      setLoading(false);
    }
  };

  if (loading || !currentDate) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl">
      {/* En-tête */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin/dashboard')}
        >
          Retour au tableau de bord
        </Button>
        <Button
          variant="contained"
          startIcon={<GetAppIcon />}
          onClick={exportToPDF}
        >
          Exporter en PDF
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
          <CircularProgress />
        </Box>
      )}

      <Paper sx={{ p: 3 }} id="calendar">
        {/* Titre avec nom de l'établissement */}
        <Typography variant="h4" component="h1" gutterBottom align="center">
          {establishmentInfo?.name || 'Calendrier Scolaire'}
        </Typography>
        
        {/* Sous-titre année scolaire */}
        <Typography variant="h5" gutterBottom align="center" sx={{ mb: 4, color: 'text.secondary' }}>
          Année scolaire {establishmentInfo?.anneeScolaire || `${new Date(anneeDebut, moisDebut, 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })} - ${new Date(anneeFin, moisFin, 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}`}
        </Typography>

        {/* Grille des mois */}
        <Grid container spacing={2}>
          {months.map((month, index) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
              {renderMonth(month)}
            </Grid>
          ))}
        </Grid>

        {/* Légende */}
        <Box sx={{ mt: 4, display: 'flex', gap: 3, flexWrap: 'wrap', justifyContent: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ 
              width: 16, 
              height: 16, 
              bgcolor: '#ffebee', 
              borderRadius: 1,
              border: '1px solid #c62828'
            }} />
            <Typography variant="body2">Jours fériés</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ 
              width: 16, 
              height: 16, 
              bgcolor: '#a5d6a7', 
              borderRadius: 1,
              border: '1px solid #2e7d32',
            }} />
            <Typography variant="body2">Vacances de printemps</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ 
              width: 16, 
              height: 16, 
              bgcolor: '#fff59d', 
              borderRadius: 1,
              border: '1px solid #fbc02d',
            }} />
            <Typography variant="body2">Vacances d'été</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ 
              width: 16, 
              height: 16, 
              bgcolor: '#ffcc80', 
              borderRadius: 1,
              border: '1px solid #f57c00',
            }} />
            <Typography variant="body2">Vacances d'automne</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ 
              width: 16, 
              height: 16, 
              bgcolor: '#90caf9', 
              borderRadius: 1,
              border: '1px solid #1976d2',
            }} />
            <Typography variant="body2">Vacances d'hiver</Typography>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default SchoolCalendar;
